import React, { useState, useEffect } from 'react';
import api from '../../../../api/client';
import {
    Box,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    IconButton,
    CircularProgress
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import { IoArchiveOutline } from "react-icons/io5";
import DynamicBasicDialog from '../../component/DynamicBasicDialog';
import Pagination from '../../component/Pagination';
import NotificationPopup from '../../component/NotificationPopup';
import AddEditCompanyDialog from '../../component/AddEditCompanyDialog';
import '../../admin.css';


const formatDate = (dateString) => {
    if (!dateString) return '—';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
    });
};

export default function Company({ onLogout }) {
    const [companies, setCompanies] = useState([]);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [showCompanyModel, setShowCompanyModel] = useState(false);
    const [id, setId] = useState(null);
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);
    const [loading, setLoading] = useState(true); // 🔄 New loading state
    const [snackbar, setSnackbar] = useState({
        open: false,
        message: '',
        severity: 'success',
    });
    const [mode, setMode] = useState('');
    const [existingCompany, setExistingCompany] = useState(null);

    const handleClose = () => {
        setSnackbar(prev => ({ ...prev, open: false }));
    };

    useEffect(() => {
        fetchCompanies();
    }, []);

    const fetchCompanies = async () => {
        setLoading(true);
        try {
            const res = await api.get('companies/', {
                headers: {
                    'Authorization': `${localStorage.getItem('adminToken')}`,
                },
            });
            const companyData = res.data.results || res.data;
            setCompanies(Array.isArray(companyData) ? companyData : []);
        } catch (err) {
            setCompanies([]);
        } finally {
            setLoading(false);
        }
    };

    const handleDeleteOpen = (id) => {
        setId(id);
        setDialogOpen(true);
    };

    const handleDeleteCompany = async () => {
        try {
            await api.delete(`companies/${id}/`);
            fetchCompanies();
            setDialogOpen(false);
            setSnackbar({ open: true, message: 'Company achived successfully', severity: 'success' });
        } catch (err) {
            setSnackbar({ open: true, message: 'Failed to archive Company. Please try again.', severity: 'error' });
        }
    };

    const openCompanyDetails = (company) => {
        setShowCompanyModel(true);
        setMode("Edit");
        setExistingCompany(company);
    };

    const handleAddCompanyOpen = () => {
        setShowCompanyModel(true);
        setMode("Add");
        setExistingCompany(null);
    };

    const handleAddCompany = async (formData) => {
        try {
            await api.post('companies/', formData);
            setShowCompanyModel(false);
            fetchCompanies();
            setSnackbar({ open: true, message: 'Company added successfully', severity: 'success' });
        } catch (err) {
            const msg = err.response?.data?.error || 'Failed to add company. Please try again.';
            setSnackbar({ open: true, message: msg, severity: 'error' });
        }
    };

    const handleEditCompany = async (formData) => {
        try {
            await api.patch('companies/', formData);
            setShowCompanyModel(false);
            fetchCompanies();
            setSnackbar({ open: true, message: 'Company updated successfully', severity: 'success' });
        } catch (err) {
            const msg = err.response?.data?.error || 'Failed to update company. Please try again.';
            setSnackbar({ open: true, message: msg, severity: 'error' });
        }
    };

    const totalCompanies = companies.length;
    const totalPages = Math.ceil(totalCompanies / pageSize);
    const paginatedCompanies = companies.slice((page - 1) * pageSize, page * pageSize);

    return (
        <div style={{ background: '#e3effa', minHeight: '100vh' }}>
            <div className="container" style={{ maxWidth: '1400px', margin: '2rem auto' }}>
                <div className="card" style={{
                    padding: '2rem',
                    borderRadius: '16px',
                    boxShadow: '0 4px 24px rgba(25, 118, 210, 0.08)',
                    background: '#fff',
                    marginTop: '2rem',
                    width: '100%'
                }}>
                    <div style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        borderBottom: '1px solid #e0e0e0',
                        paddingBottom: '1rem'
                    }}>
                        <div>
                            <h2 style={{
                                margin: 0,
                                color: '#333',
                                marginBottom: '0.5rem'
                            }}>Companies</h2>
                            <p style={{
                                margin: 0,
                                color: '#666',
                            }}>Manage Companies</p>
                        </div>
                        <button
                            className="button"
                            style={{
                                background: '#1976d2',
                                color: '#fff',
                                border: 'none',
                                borderRadius: '6px',
                                padding: '0.7rem 1.4rem',
                                cursor: 'pointer',
                                boxShadow: '0 4px 12px rgba(25, 118, 210, 0.15)',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '8px',
                                transition: 'all 0.2s ease'
                            }}
                            onMouseOver={e => e.currentTarget.style.background = '#1565c0'}
                            onMouseOut={e => e.currentTarget.style.background = '#1976d2'}
                            onClick={handleAddCompanyOpen}
                        >
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <path d="M19 13h-6v6h-2v-6H5v-2h6V5h2v6h6v2z" fill="white" />
                            </svg>
                            Add New Company
                        </button>
                    </div>

                    <div style={{ overflowX: 'auto', width: '100%' }}>
                        <TableContainer component={Paper}>
                            <Table sx={{ minWidth: 650 }} aria-label="company table">
                                <TableHead>
                                    <TableRow>
                                        <TableCell>Name</TableCell>
                                        <TableCell align="left">Description</TableCell>
                                        <TableCell align="left">Website</TableCell>
                                        <TableCell align="left">Industry</TableCell>
                                        <TableCell align="left">Created</TableCell>
                                        <TableCell align="center">Actions</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {loading ? (
                                        <TableRow>
                                            <TableCell colSpan={6} align="center">
                                                <CircularProgress size={24} />
                                            </TableCell>
                                        </TableRow>
                                    ) : paginatedCompanies.length === 0 ? (
                                        <TableRow>
                                            <TableCell colSpan={6} align="center">
                                                No record found
                                            </TableCell>
                                        </TableRow>
                                    ) : (
                                        paginatedCompanies.map((row) => (
                                            <TableRow key={row.id}>
                                                <TableCell>{row.name}</TableCell>
                                                <TableCell align="left">{row.description}</TableCell>
                                                <TableCell align="left">{row.website}</TableCell>
                                                <TableCell align="left">{row.industry}</TableCell>
                                                <TableCell align="left">{formatDate(row.created_at)}</TableCell>
                                                <TableCell align="center">
                                                    <Box display="flex" justifyContent="center" gap={1}>
                                                        <IconButton onClick={() => openCompanyDetails(row)}>
                                                            <EditIcon color="primary" />
                                                        </IconButton>
                                                        <IconButton onClick={() => handleDeleteOpen(row.id)}>
                                                            <IoArchiveOutline color="error" />
                                                        </IconButton>
                                                    </Box>
                                                </TableCell>
                                            </TableRow>
                                        ))
                                    )}
                                </TableBody>
                            </Table>
                        </TableContainer>
                        {totalPages > 1 && (
                            <Pagination
                                currentPage={page}
                                totalPages={totalPages}
                                totalCount={totalCompanies}
                                currentCount={paginatedCompanies.length}
                                onPageChange={setPage}
                                onPageSizeChange={setPageSize}
                                pageSize={pageSize}
                                showPageSize={true}
                                pageSizeOptions={[10, 25, 50]}
                                hasActiveFilters={false} // Set to true if you add filtering functionality
                            />
                        )}
                    </div>
                </div>
            </div>

            <AddEditCompanyDialog
                mode={mode}
                existingCompany={existingCompany}
                onSubmit={(formData) => {
                    mode === "Add" ? handleAddCompany(formData) : handleEditCompany(formData);
                }}
                open={showCompanyModel}
                onClose={() => setShowCompanyModel(false)}
            />

            <DynamicBasicDialog
                open={dialogOpen}
                onClose={() => setDialogOpen(false)}
                onConfirm={handleDeleteCompany}
                title="Archive Confirmation"
                content="Are you sure you want to archive this company?"
            />

            <NotificationPopup
                open={snackbar.open}
                onClose={handleClose}
                severity={snackbar.severity}
                message={snackbar.message}
            />
        </div>
    );
}
